import {
  FUND_BASKET_STRATEGY_OPTIONS,
  FUND_BASKET_TENURE_OPTIONS,
} from "creso-core/constants";
import { FundBasketSummary } from "creso-core/types";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "web-design-system";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { EllipsisVerticalIcon, PencilIcon } from "lucide-react";
import { formatAsPercentage } from "creso-core/utils";
import { Link } from "@tanstack/react-router";
import { BasketIcon } from "./icon";
import { DeleteFundBasket } from "./delete-fund-basket";
import { DuplicateFundBasket } from "./duplicate-fund-basket";
import { FundBasketDialog } from "./dialog";
import { BasketAllocationLineChart } from "./basket-allocation-line-chart";

type StuckEdge = "top" | "bottom" | null;

function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null;
  let p: HTMLElement | null = el.parentElement;
  while (p) {
    const { overflowY, overflow } = getComputedStyle(p);
    if (
      (/(auto|scroll|overlay)/.test(overflowY) ||
        /(auto|scroll|overlay)/.test(overflow)) &&
      p.scrollHeight > p.clientHeight
    ) {
      return p;
    }
    p = p.parentElement;
  }
  return null;
}

/** Where a `position: sticky` element is currently pinned, or `null` if in normal flow. */
const useStuckEdge = (stickyEnabled: boolean) => {
  const [stuckEdge, setStuckEdge] = useState<StuckEdge>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [distanceTop, setDistanceTop] = useState<number | null>();
  const [distanceBottom, setDistanceBottom] = useState<number | null>();

  useLayoutEffect(() => {
    if (!stickyEnabled) {
      setStuckEdge(null);
      setDistanceTop(null);
      setDistanceBottom(null);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const scroller = getScrollParent(el) ?? window;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const style = getComputedStyle(el);
      const rootRect =
        scroller instanceof HTMLElement
          ? scroller.getBoundingClientRect()
          : { top: 0, bottom: window.innerHeight };

      const topInset = parseFloat(style.top) || 0;
      const bottomInset =
        style.bottom === "auto" ? 0 : parseFloat(style.bottom) || 0;
      const tol = 2;

      const dTop = Math.abs(rect.top - (rootRect.top + topInset));
      const dBottom = Math.abs(rect.bottom - (rootRect.bottom - bottomInset));

      setDistanceBottom(() => (dBottom > 16 ? null : dBottom));
      setDistanceTop(() => (dTop > 16 ? null : dTop));

      const atTop = dTop < tol;
      const atBottom = dBottom < tol;

      if (atTop && atBottom) {
        setStuckEdge(dTop <= dBottom ? "top" : "bottom");
      } else if (atTop) {
        setStuckEdge("top");
      } else if (atBottom) {
        setStuckEdge("bottom");
      } else {
        setStuckEdge(null);
      }
    };

    scroller.addEventListener("scroll", update, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", update, { passive: true });

    const ro = new ResizeObserver(update);
    ro.observe(el);

    update();

    // eslint-disable-next-line
    return () => {
      scroller.removeEventListener("scroll", update, {
        capture: true,
      } as AddEventListenerOptions);
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, [stickyEnabled]);

  return { stuckEdge, ref, distanceTop, distanceBottom } as const;
};

export function FundBasketListItem(props: {
  basket: FundBasketSummary;
  isSelected: boolean;
  onSelect: (basket: FundBasketSummary) => void;
  className?: string;
  onStick?: (edge: "top" | "bottom" | null) => void;
  withDivider?: boolean;
  collapsed: boolean;
}) {
  const { basket, isSelected, onSelect, onStick, withDivider, collapsed } =
    props;

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { stuckEdge, ref, distanceTop, distanceBottom } =
    useStuckEdge(isSelected);

  useEffect(() => {
    onStick?.(stuckEdge);
  }, [stuckEdge, onStick]);

  const description = useMemo(() => {
    if (!basket) return null;
    const tenure = FUND_BASKET_TENURE_OPTIONS.find(
      (f) => f.value === basket.tenure
    )?.label;
    const strategy = FUND_BASKET_STRATEGY_OPTIONS.find(
      (f) => f.value === basket.strategy
    )?.label;
    return (
      <>
        {tenure} &bull; {strategy}
      </>
    );
  }, [basket]);

  return (
    <div
      ref={ref}
      data-selected={isSelected}
      data-sticky={stuckEdge}
      className={cn(
        "hover:bg-muted h-24",
        "px-4 py-6 relative grid rounded-lg items-center gap-6 grid-cols-13 border border-transparent border-r-0",
        props.className,
        !isSelected && "bg-background",
        isSelected && "sticky top-31 bottom-5 z-10 rounded-l-lg border-border",
        // to make the bottom sync with that of the details card
        stuckEdge === "top" && "rounded-tr-none",
        stuckEdge === "bottom" && "rounded-br-none"
      )}
      onClick={() => onSelect(basket)}
    >
      <ConditionalRenderer condition={isSelected}>
        <div
          className="absolute rounded-l-lg h-full w-[calc(100%+var(--spacing)*5)] left-0 top-0"
          style={{
            background:
              "linear-gradient(to right, color-mix(in srgb, var(--color-tag-yellow) 50%, var(--color-background)), color-mix(in srgb, var(--color-tag-yellow) 30%, var(--color-background)))",
          }}
        />
        <ConditionalRenderer condition={stuckEdge !== "top"}>
          <div
            style={
              distanceTop
                ? {
                    maxHeight: `${distanceTop / 2}px`,
                  }
                : {}
            }
            className="absolute -right-px w-4 -z-1 top-0 -translate-y-full h-4 bg-tag-yellow"
          >
            <div
              style={
                distanceTop
                  ? { borderBottomRightRadius: `${distanceTop / 2}px` }
                  : {}
              }
              className="size-full border border-border border-l-0 box-border border-t-0 rounded-br-lg bg-background [:has(>div:hover+[data-selected=true])_[data-selected=true]_&]:bg-muted"
            />
          </div>
        </ConditionalRenderer>
        <ConditionalRenderer condition={stuckEdge !== "bottom"}>
          <div
            style={distanceBottom ? { maxHeight: `${distanceBottom}px` } : {}}
            className="absolute -right-px w-4 -z-1 bottom-0 translate-y-full h-4 bg-tag-yellow"
          >
            <div
              style={
                distanceBottom
                  ? { borderTopRightRadius: `${distanceBottom / 2}px` }
                  : {}
              }
              className="size-full border border-border border-l-0 box-border border-b-0 rounded-tr-lg bg-background [[data-selected=true]:has(+_div:hover)_&]:bg-muted"
            />
          </div>
        </ConditionalRenderer>
      </ConditionalRenderer>
      <div
        className={cn(
          "absolute h-px -bottom-px bg-secondary w-[calc(100%-var(--spacing)*6)]",
          (!withDivider || isSelected) && "hidden"
        )}
      />
      <div
        className={cn("flex gap-3 z-2 col-span-5", collapsed && "col-span-8")}
      >
        <BasketIcon strategy={basket.strategy} />
        <div className="flex flex-col gap-0.5 min-w-0">
          <Link
            to="/funds/basket/$basket_id"
            params={{ basket_id: basket.id }}
            className="text-sm font-semibold"
            onClick={(e) => e.stopPropagation()}
          >
            {basket.name}
          </Link>
          <div className="flex gap-2 items-center">
            <span className="text-xs text-muted-foreground">{description}</span>
            {basket.basket_type === "curated" ? (
              <Badge
                variant="custom"
                className="text-tag-yellow-foreground bg-tag-yellow"
              >
                Creso curated
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
      <div className={cn("col-span-4 z-2", collapsed && "hidden")}>
        <BasketAllocationLineChart basket={basket} />
      </div>
      {/* <div className={cn("col-span-3 z-2", collapsed && "hidden")}>
        <div className="bg-muted p-2 px-3 w-full rounded-md justify-between items-center flex">
          <AmcLogoGroup size="sm" items={basket.funds_summary} />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">
              {formatAsPercentage(basket.avg_returns_3y, "-")}
            </span>
            <span className="text-xs text-muted-foreground">3Y avg</span>
          </div>
        </div>
      </div> */}
      <div className={cn("flex-col gap-1 flex z-2 items-end col-span-3")}>
        <span className="text-sm font-semibold">
          {formatAsPercentage(basket.avg_returns_3y, "-")}
        </span>
        <span className="text-xs text-muted-foreground">3Y avg</span>
      </div>
      <div
        onClick={(e) => e.stopPropagation()}
        className="pointer-events-auto flex justify-end shrink-0 z-2 col-span-1"
      >
        <DropdownMenu>
          <div className="group">
            <DropdownMenuTrigger asChild>
              <Button
                onClick={(e) => e.stopPropagation()}
                variant="ghost"
                size="icon"
              >
                <EllipsisVerticalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <ConditionalRenderer condition={basket.basket_type === "custom"}>
                <DropdownMenuItem
                  onSelect={() => {
                    setIsEditDialogOpen(true);
                  }}
                >
                  <PencilIcon />
                  Edit Basket
                </DropdownMenuItem>
              </ConditionalRenderer>
              <DuplicateFundBasket id={props.basket.id} />
              <ConditionalRenderer condition={basket.basket_type === "custom"}>
                <DropdownMenuItem asChild className="text-destructive">
                  <DeleteFundBasket id={props.basket.id} />
                </DropdownMenuItem>
              </ConditionalRenderer>
            </DropdownMenuContent>
          </div>
        </DropdownMenu>
        {isEditDialogOpen && (
          <FundBasketDialog
            basketId={props.basket.id}
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
          />
        )}
      </div>
    </div>
  );
}
